import { ROUTES } from 'constant/routes';

import type { SystemModulesResponse } from 'types';

export type DashboardSectionKey =
  | 'appointments'
  | 'secureMessages'
  | 'forms'
  | 'services'
  | 'packages'
  | 'products'
  | 'purchases'
  | 'memberships'
  | 'giftCards'
  | 'addresses'
  | 'paymentMethods'
  | 'preferences'
  | 'terms'
  | 'careers'
  | 'privacyPolicy';

export interface DashboardSectionDescriptor {
  key: DashboardSectionKey;
  /** ROUTES.<x> for routed sections; undefined for footer-only entries. */
  route?: string;
  /** Matches the `id` field on a tenant's `footerMenu` config entries; only set for footer-only sections. */
  footerId?: string;
  sysPrefField: keyof SystemModulesResponse;
}

/**
 * My Profile (`ROUTES.profile`) and My Dashboard (`ROUTES.dashboard`) are deliberately not
 * registered here — they're always on. `filterDashboardNavItems`/`filterFooterMenuItems`
 * default to "keep" for any link that isn't in this registry, which covers those two plus any
 * tenant-custom nav/footer entries that aren't part of the toggleable set (e.g. `sbl`'s
 * "Shop Policies", `hrimn`'s "Contact Us").
 */
export const DASHBOARD_SECTIONS: DashboardSectionDescriptor[] = [
  {
    key: 'appointments',
    route: ROUTES.appointments,
    sysPrefField: 'show_acct_appointments'
  },
  {
    key: 'secureMessages',
    route: ROUTES.communication,
    sysPrefField: 'show_acct_messages'
  },
  { key: 'forms', route: ROUTES.forms, sysPrefField: 'show_acct_forms' },
  {
    key: 'services',
    route: ROUTES.services,
    sysPrefField: 'show_acct_services'
  },
  {
    key: 'packages',
    route: ROUTES.packages,
    sysPrefField: 'show_acct_packages'
  },
  {
    key: 'products',
    route: ROUTES.products,
    sysPrefField: 'show_acct_products'
  },
  {
    key: 'purchases',
    route: ROUTES.orders,
    sysPrefField: 'show_acct_purchases'
  },
  {
    key: 'memberships',
    route: ROUTES.memberships,
    sysPrefField: 'show_acct_memberships'
  },
  {
    key: 'giftCards',
    route: ROUTES.giftCards,
    sysPrefField: 'show_acct_gift_cards'
  },
  {
    key: 'addresses',
    route: ROUTES.addresses,
    sysPrefField: 'show_acct_addresses'
  },
  {
    key: 'paymentMethods',
    route: ROUTES.creditCards,
    sysPrefField: 'show_acct_payment_methods'
  },
  {
    key: 'preferences',
    route: ROUTES.preferences,
    sysPrefField: 'show_acct_preferences'
  },
  { key: 'terms', footerId: 'terms', sysPrefField: 'show_acct_terms' },
  { key: 'careers', footerId: 'careers', sysPrefField: 'show_acct_careers' },
  {
    key: 'privacyPolicy',
    footerId: 'privacyPolicy',
    sysPrefField: 'show_acct_privacy_policy'
  }
];

export const DASHBOARD_SECTION_BY_KEY: Record<
  DashboardSectionKey,
  DashboardSectionDescriptor
> = DASHBOARD_SECTIONS.reduce(
  (acc, section) => {
    acc[section.key] = section;
    return acc;
  },
  {} as Record<DashboardSectionKey, DashboardSectionDescriptor>
);

export const DASHBOARD_SECTION_BY_ROUTE: Partial<
  Record<string, DashboardSectionKey>
> = DASHBOARD_SECTIONS.reduce(
  (acc, section) => {
    if (section.route) acc[section.route] = section.key;
    return acc;
  },
  {} as Partial<Record<string, DashboardSectionKey>>
);

export const DASHBOARD_SECTION_BY_FOOTER_ID: Partial<
  Record<string, DashboardSectionKey>
> = DASHBOARD_SECTIONS.reduce(
  (acc, section) => {
    if (section.footerId) acc[section.footerId] = section.key;
    return acc;
  },
  {} as Partial<Record<string, DashboardSectionKey>>
);
